import { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';

import { ClampedText } from '@/components/shared/components/ClampedText';
import { FormattedText } from '@/components/shared/components/FormattedText';
import { Linkify } from '@/components/shared/components/Linkify';
import {
    SUCCESS_BANNER_SECONDS,
    SuccessBanner,
    useSuccessBannerToast,
} from '@/components/shared/components/SuccessBanner';
import { SuccessBannerDescription } from '@/components/shared/components/SuccessBanner/SuccessBanner.StyledComponents';
import { useFeatureFlags } from '@/components/shared/constants/featureFlags';

import { firstLineAsTitle } from './ApplicantConditions.utils';

export { SUCCESS_BANNER_SECONDS };

export type ConditionSuccessBannerDetails =
    | { kind: 'created' | 'resolved'; fileCount?: undefined }
    | { kind: 'attached'; fileCount: number };

export type ConditionSuccessBannerKind = ConditionSuccessBannerDetails['kind'];

const TITLES: Record<'created' | 'resolved', string> = {
    created: '✓ Task/Condition Created:',
    resolved: '✓ Task/Condition Resolved:',
};

const PLAIN_TEXT_ELEMENTS: string[] = [];
const TITLE_PLUGINS = [firstLineAsTitle];

const getAttachedTitle = (fileCount: number, description: string) => (
    <>
        {`✓ ${fileCount} ${fileCount === 1 ? 'file' : 'files'} attached`}
        <ReactMarkdown
            remarkPlugins={TITLE_PLUGINS}
            allowedElements={PLAIN_TEXT_ELEMENTS}
            unwrapDisallowed
        >
            {description}
        </ReactMarkdown>
    </>
);

type ContentProps = ConditionSuccessBannerDetails & {
    description: string;
    onViewNow: () => void;
    onDismiss: () => void;
};

export const ConditionSuccessBannerContent = (props: ContentProps) => {
    const { description, onViewNow, onDismiss } = props;
    const { isConditionTextFormattingEnabled } = useFeatureFlags();
    const attachedTitle = useMemo(
        () => (props.kind === 'attached' ? getAttachedTitle(props.fileCount, description) : null),
        [props.kind, props.fileCount, description],
    );

    return (
        <SuccessBanner
            testIdPrefix="condition-success-banner"
            rootTestId={`condition-success-banner-${props.kind}`}
            title={
                props.kind === 'attached' ? (
                    <ClampedText expanded={false} lines={3}>
                        {attachedTitle}
                    </ClampedText>
                ) : (
                    TITLES[props.kind]
                )
            }
            onViewNow={onViewNow}
            onDismiss={onDismiss}
        >
            {props.kind !== 'attached' && (
                <SuccessBannerDescription
                    $markdown={isConditionTextFormattingEnabled}
                    data-testid="condition-success-banner-description"
                >
                    <ClampedText expanded={false} lines={4}>
                        {isConditionTextFormattingEnabled ? (
                            <FormattedText>{description}</FormattedText>
                        ) : (
                            <Linkify>{description}</Linkify>
                        )}
                    </ClampedText>
                </SuccessBannerDescription>
            )}
        </SuccessBanner>
    );
};

type ShowBannerOptions = ConditionSuccessBannerDetails & {
    description: string;
    onViewNow: () => void;
};

export const useConditionSuccessBanner = () => {
    const { banner, showBanner: showToastBanner } = useSuccessBannerToast(
        'condition-success-banners',
        'condition-success-banner',
    );

    const showBanner = (options: ShowBannerOptions) =>
        showToastBanner((dismiss) => (
            <ConditionSuccessBannerContent
                {...options}
                onDismiss={dismiss}
                onViewNow={() => {
                    dismiss();
                    options.onViewNow();
                }}
            />
        ));

    return { banner, showBanner };
};
